import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  PAYMENTS_PERIOD_OPTIONS,
  paymentsPeriodLabel,
  type PaymentsPeriodFilter,
} from '../../payments-period-filter.util';
import { PaymentsLocationOption } from '../../payments-location-filter.util';
import {
  DateRange,
  DateRangePickerComponent,
} from '../../../shared/components/date-range-picker/date-range-picker.component';
import { ModalFooterActionsComponent } from '../../../shared/components/modal-footer-actions/modal-footer-actions.component';

export interface PaymentsToolbarStatusOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-payments-toolbar-filters',
  standalone: true,
  imports: [CommonModule, FormsModule, DateRangePickerComponent, ModalFooterActionsComponent],
  templateUrl: './payments-toolbar-filters.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './payments-toolbar-filters.component.scss',
})
export class PaymentsToolbarFiltersComponent implements OnChanges {
  @Input() statusOptions: PaymentsToolbarStatusOption[] = [];
  @Input() selectedStatuses: string[] = [];
  @Output() selectedStatusesChange = new EventEmitter<string[]>();

  @Input() selectedPeriod: PaymentsPeriodFilter = 'today';
  @Input() customDateRange: DateRange = { startDate: null, endDate: null };
  @Output() periodChange = new EventEmitter<PaymentsPeriodFilter>();
  @Output() dateRangeApply = new EventEmitter<{ period: string; customRange?: DateRange }>();
  @Input() showPeriodFilter = true;
  /** When false, hides the Status multi-select (e.g. Discount Custom tab). */
  @Input() showStatusFilter = true;
  /** When false, hides the Export button (e.g. Discount Custom tab). */
  @Input() showExportButton = true;

  readonly periodOptions = [...PAYMENTS_PERIOD_OPTIONS];

  @Input() statusSelectInputId = 'paymentsStatusFilter';
  @Input() exportLoading = false;
  @Output() exportClick = new EventEmitter<void>();

  @Input() locationOptions: PaymentsLocationOption[] = [];
  @Input() selectedLocationId = '';
  @Input() selectedLocationLabel = 'All Locations';
  @Output() locationChange = new EventEmitter<string>();

  isStatusDropdownOpen = false;
  isPeriodDropdownOpen = false;
  isLocationDropdownOpen = false;
  draftSelectedStatuses: string[] = [];
  /** Apply stays disabled until the draft differs from the applied status filter. */
  primaryDisabled = true;
  selectedPeriodLabel = paymentsPeriodLabel('today');

  private setDropdownOpen(dropdown: 'status' | 'period' | 'location' | null): void {
    this.isStatusDropdownOpen = dropdown === 'status';
    this.isPeriodDropdownOpen = dropdown === 'period';
    this.isLocationDropdownOpen = dropdown === 'location';
  }

  constructor(private readonly elementRef: ElementRef<HTMLElement>) {}

  ngOnChanges(): void {
    this.selectedPeriodLabel = paymentsPeriodLabel(this.selectedPeriod);
  }

  trackByPeriodValue(_: number, option: { value: string }): string {
    return option.value;
  }

  toggleStatusDropdown(event?: MouseEvent): void {
    event?.stopPropagation();
    if (this.isStatusDropdownOpen) {
      this.setDropdownOpen(null);
      return;
    }
    this.resetDraftFromAppliedStatuses();
    this.setDropdownOpen('status');
  }

  onStatusApply(): void {
    if (this.primaryDisabled) {
      return;
    }
    this.selectedStatuses = [...this.draftSelectedStatuses];
    this.selectedStatusesChange.emit(this.selectedStatuses);
    this.refreshStatusApplyDisabled();
    this.setDropdownOpen(null);
  }

  onStatusCancel(): void {
    this.resetDraftFromAppliedStatuses();
    this.setDropdownOpen(null);
  }

  onStatusClear(): void {
    this.draftSelectedStatuses = [];
    const hadAppliedStatuses = this.selectedStatuses.length > 0;
    this.selectedStatuses = [];
    this.refreshStatusApplyDisabled();
    if (hadAppliedStatuses) {
      this.selectedStatusesChange.emit([]);
    }
    this.setDropdownOpen(null);
  }

  onDraftStatusToggle(value: string, checked: boolean): void {
    if (checked) {
      if (!this.draftSelectedStatuses.includes(value)) {
        this.draftSelectedStatuses = [...this.draftSelectedStatuses, value];
      }
    } else {
      this.draftSelectedStatuses = this.draftSelectedStatuses.filter(s => s !== value);
    }
    this.refreshStatusApplyDisabled();
  }

  private resetDraftFromAppliedStatuses(): void {
    this.draftSelectedStatuses = [...this.selectedStatuses];
    this.refreshStatusApplyDisabled();
  }

  private refreshStatusApplyDisabled(): void {
    this.primaryDisabled = !this.hasDraftStatusChanges();
  }

  private hasDraftStatusChanges(): boolean {
    const draftKey = [...this.draftSelectedStatuses].sort().join(',');
    const appliedKey = [...this.selectedStatuses].sort().join(',');
    return draftKey !== appliedKey;
  }

  toggleLocationDropdown(event?: MouseEvent): void {
    event?.stopPropagation();
    this.setDropdownOpen(this.isLocationDropdownOpen ? null : 'location');
  }

  selectLocation(locationId: string): void {
    this.setDropdownOpen(null);
    this.locationChange.emit(locationId);
  }

  togglePeriodDropdown(event?: MouseEvent): void {
    event?.stopPropagation();
    this.setDropdownOpen(this.isPeriodDropdownOpen ? null : 'period');
  }

  closePeriodDropdown(): void {
    this.isPeriodDropdownOpen = false;
  }

  onDateRangeApply(data: { period: string; customRange?: DateRange }): void {
    this.selectedPeriodLabel = paymentsPeriodLabel(data.period);
    this.setDropdownOpen(null);
    this.dateRangeApply.emit(data);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const periodDropdown =
      target.closest('.period-dropdown-wrapper') || target.closest('.date-filter-wrap');
    const dateRangePicker = target.closest('.dropdown-menu');
    const customDatePicker = target.closest('.custom-date-picker');
    const primeCalendar = target.closest('.p-datepicker') || target.closest('.p-calendar');
    const primeCalendarPanel = target.closest('.p-datepicker-panel');

    if (
      this.isPeriodDropdownOpen &&
      (periodDropdown || dateRangePicker || customDatePicker || primeCalendar || primeCalendarPanel)
    ) {
      return;
    }

    if (!this.elementRef.nativeElement.contains(target as Node)) {
      if (this.isStatusDropdownOpen) {
        this.resetDraftFromAppliedStatuses();
      }
      this.setDropdownOpen(null);
    }
  }
}
